/**
 * Web-based push provider using the Push API.
 *
 * @module
 */
import { t } from '@molecule/app-i18n';
/**
 * Creates a web push provider using the browser Push API and Notification API.
 * @param vapidPublicKey - Optional VAPID public key for server-authenticated subscriptions.
 * @returns A {@link PushProvider} backed by the Web Push API.
 */
export const createWebPushProvider = (vapidPublicKey) => {
    const notificationReceivedListeners = new Set();
    const notificationActionListeners = new Set();
    const tokenChangeListeners = new Set();
    let currentToken = null;
    let registration = null;
    /**
     * Resolve the active service worker registration WITHOUT hanging.
     *
     * `navigator.serviceWorker.ready` never settles when no service worker is
     * registered (the common case in dev builds — only production builds ship
     * one), which used to freeze the enable-push UI forever. `getRegistration()`
     * resolves immediately (`undefined` when none), so callers can fail fast or
     * degrade gracefully; when a registration exists, awaiting `ready` is then
     * safe (it settles once the worker activates).
     */
    const getReadyRegistration = async () => {
        const existing = await navigator.serviceWorker.getRegistration();
        if (!existing)
            return null;
        return navigator.serviceWorker.ready;
    };
    // Convert VAPID key to Uint8Array
    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };
    return {
        async checkPermission() {
            if (!('Notification' in window)) {
                return 'denied';
            }
            return Notification.permission;
        },
        async requestPermission() {
            if (!('Notification' in window)) {
                return 'denied';
            }
            const result = await Notification.requestPermission();
            return result;
        },
        async register(options) {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                throw new Error(t('push.error.notSupported', undefined, {
                    defaultValue: 'Push notifications not supported',
                }));
            }
            // Fail fast (never hang) when no service worker is registered — dev
            // builds don't ship one, and awaiting `.ready` would block forever.
            registration = await getReadyRegistration();
            if (!registration) {
                throw new Error(t('push.error.noServiceWorker', undefined, {
                    defaultValue: 'No service worker is registered — push notifications require the production app build.',
                }));
            }
            // Subscribe to push
            const subscribeOptions = {
                userVisibleOnly: true,
            };
            // Runtime key (e.g. fetched from the api) wins over the constructor key.
            const applicationServerKey = options?.vapidPublicKey ?? vapidPublicKey;
            if (applicationServerKey) {
                subscribeOptions.applicationServerKey = urlBase64ToUint8Array(applicationServerKey)
                    .buffer;
            }
            const subscription = await registration.pushManager.subscribe(subscribeOptions);
            const token = {
                value: JSON.stringify(subscription.toJSON()),
                platform: 'web',
                timestamp: Date.now(),
            };
            currentToken = token;
            tokenChangeListeners.forEach((listener) => listener(token));
            return token;
        },
        async unregister() {
            if (!registration) {
                registration = await getReadyRegistration();
            }
            if (!registration) {
                // Nothing to unsubscribe from (no service worker → no subscription).
                currentToken = null;
                return;
            }
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
            }
            currentToken = null;
        },
        async getToken() {
            if (currentToken)
                return currentToken;
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                return null;
            }
            registration = await getReadyRegistration();
            if (!registration)
                return null;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                currentToken = {
                    value: JSON.stringify(subscription.toJSON()),
                    platform: 'web',
                    timestamp: Date.now(),
                };
                return currentToken;
            }
            return null;
        },
        onNotificationReceived(listener) {
            notificationReceivedListeners.add(listener);
            return () => notificationReceivedListeners.delete(listener);
        },
        onNotificationAction(listener) {
            notificationActionListeners.add(listener);
            return () => notificationActionListeners.delete(listener);
        },
        onTokenChange(listener) {
            tokenChangeListeners.add(listener);
            return () => tokenChangeListeners.delete(listener);
        },
        async scheduleLocal(options) {
            const permission = await this.checkPermission();
            if (permission !== 'granted') {
                throw new Error(t('push.error.permissionNotGranted', undefined, {
                    defaultValue: 'Notification permission not granted',
                }));
            }
            const id = options.id || Math.random().toString(36).slice(2);
            const showNotification = () => {
                if (registration) {
                    const notificationOptions = {
                        body: options.body,
                        data: { id, ...options.extra },
                        badge: options.badge ? String(options.badge) : undefined,
                        tag: id,
                    };
                    if (options.actions) {
                        notificationOptions.actions = options.actions.map((a) => ({
                            action: a.id,
                            title: a.title,
                            icon: a.icon,
                        }));
                    }
                    registration.showNotification(options.title, notificationOptions);
                }
                else {
                    new Notification(options.title, {
                        body: options.body,
                        data: { id, ...options.extra },
                        tag: id,
                    });
                }
            };
            if (options.at) {
                const delay = options.at.getTime() - Date.now();
                if (delay > 0) {
                    setTimeout(showNotification, delay);
                }
                else {
                    showNotification();
                }
            }
            else {
                showNotification();
            }
            return id;
        },
        async cancelLocal(_id) {
            // Web API doesn't support canceling scheduled notifications easily
            // This would require tracking timers
        },
        async cancelAllLocal() {
            // Same limitation
        },
        async getPendingLocal() {
            // Web API doesn't expose pending notifications
            return [];
        },
        async getDelivered() {
            if (!registration) {
                registration = await getReadyRegistration();
            }
            if (!registration)
                return [];
            const notifications = await registration.getNotifications();
            return notifications.map((n) => {
                const extN = n;
                return {
                    id: n.tag || Math.random().toString(36).slice(2),
                    title: n.title,
                    body: n.body || undefined,
                    data: n.data,
                    icon: n.icon || undefined,
                    image: extN.image || undefined,
                    tag: n.tag || undefined,
                    timestamp: extN.timestamp,
                };
            });
        },
        async removeDelivered(ids) {
            if (!registration) {
                registration = await getReadyRegistration();
            }
            if (!registration)
                return;
            const notifications = await registration.getNotifications();
            for (const n of notifications) {
                if (ids.includes(n.tag || '')) {
                    n.close();
                }
            }
        },
        async removeAllDelivered() {
            if (!registration) {
                registration = await getReadyRegistration();
            }
            if (!registration)
                return;
            const notifications = await registration.getNotifications();
            for (const n of notifications) {
                n.close();
            }
        },
        async setBadge(count) {
            if ('setAppBadge' in navigator) {
                await navigator.setAppBadge(count);
            }
        },
        async getBadge() {
            // Web API doesn't support reading badge
            return 0;
        },
        async clearBadge() {
            if ('clearAppBadge' in navigator) {
                await navigator.clearAppBadge();
            }
        },
        destroy() {
            notificationReceivedListeners.clear();
            notificationActionListeners.clear();
            tokenChangeListeners.clear();
        },
    };
};
//# sourceMappingURL=web-provider.js.map